import { EXPORT_FORMAT } from "./lottery.enums";
import { z } from "zod";

// Base ticket schema
export const LotteryTicketSchema = z.object({
  id: z.string(),
  earnedAt: z.coerce.date(),
  source: z.string(),
  questCompletionId: z.string().optional(),
});

export type LotteryTicket = z.infer<typeof LotteryTicketSchema>;

// Current draw schema
export const CurrentDrawSchema = z.object({
  id: z.string(),
  name: z.string(),
  endDate: z.coerce.date(),
  drawDate: z.coerce.date(),
  totalTickets: z.number(),
  canAcceptTickets: z.boolean(),
});

export type CurrentDraw = z.infer<typeof CurrentDrawSchema>;

// Draw period schema
export const DrawPeriodSchema = z.object({
  id: z.string(),
  name: z.string(),
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
  drawDate: z.coerce.date(),
  status: z.string(),
});

export type DrawPeriod = z.infer<typeof DrawPeriodSchema>;

// User API DTOs
export const UserTicketsResponseDtoSchema = z.object({
  totalTickets: z.number(),
  tickets: z.array(LotteryTicketSchema),
  currentDraw: CurrentDrawSchema.nullable(),
});

export type UserTicketsResponseDto = z.infer<typeof UserTicketsResponseDtoSchema>;

// Time remaining schema for active draw periods
export const TimeRemainingSchema = z.object({
  days: z.number().int().min(0),
  hours: z.number().int().min(0).max(23),
  minutes: z.number().int().min(0).max(59),
});

export type TimeRemaining = z.infer<typeof TimeRemainingSchema>;

export const CurrentDrawResponseDtoSchema = z.object({
  currentDraw: DrawPeriodSchema.nullable(),
  totalTickets: z.number(),
  uniqueParticipants: z.number(),
  canAcceptTickets: z.boolean(),
  timeRemaining: TimeRemainingSchema.optional(), // Properly typed: object when active, undefined when ended
});

export type CurrentDrawResponseDto = z.infer<typeof CurrentDrawResponseDtoSchema>;

export const LotteryHistoryQueryDtoSchema = z.object({
  limit: z.coerce.number().min(1).max(100).optional().default(10),
  offset: z.coerce.number().min(0).optional().default(0),
});

export type LotteryHistoryQueryDto = z.infer<typeof LotteryHistoryQueryDtoSchema>;

export const LotteryHistoryEntrySchema = z.object({
  drawPeriodId: z.string(),
  drawPeriodName: z.string(),
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
  drawDate: z.coerce.date(),
  status: z.string(),
  userTicketCount: z.number(),
  totalTickets: z.number(),
  userParticipated: z.boolean(),
});

export type LotteryHistoryEntry = z.infer<typeof LotteryHistoryEntrySchema>;

export const LotteryHistoryResponseDtoSchema = z.object({
  history: z.array(LotteryHistoryEntrySchema),
  totalEntries: z.number(),
  hasMore: z.boolean(),
});

export type LotteryHistoryResponseDto = z.infer<typeof LotteryHistoryResponseDtoSchema>;

// Admin API DTOs
export const ExportDrawRequestDtoSchema = z.object({
  format: z.nativeEnum(EXPORT_FORMAT),
  includeMetadata: z.boolean().optional().default(false),
});

export type ExportDrawRequestDto = z.infer<typeof ExportDrawRequestDtoSchema>;

export const ParticipantSummaryDtoSchema = z.object({
  hubUserId: z.number(),
  ticketCount: z.number(),
});

export type ParticipantSummaryDto = z.infer<typeof ParticipantSummaryDtoSchema>;

export const ParticipantsResponseDtoSchema = z.object({
  drawPeriodId: z.string(),
  drawPeriodName: z.string(),
  totalTickets: z.number(),
  uniqueParticipants: z.number(),
  participants: z.array(ParticipantSummaryDtoSchema),
  canExport: z.boolean(),
  isExported: z.boolean(),
});

export type ParticipantsResponseDto = z.infer<typeof ParticipantsResponseDtoSchema>;

// Request body for admin grant lottery tickets endpoint
export const GrantLotteryTicketsRequestSchema = z.object({
  hubUserId: z.number().int().positive(),
  ticketCount: z.number().int().positive().min(1).max(100),
});

export type GrantLotteryTicketsRequest = z.infer<typeof GrantLotteryTicketsRequestSchema>;
